import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { localLLM, LocalLLMStatus } from "@/llm/localTinyLlamaManager";
import { GenerateOptions } from "@/llm/tinyllamaWebGpuRuntime";

type LocalLLMContextValue = {
  status: LocalLLMStatus;
  isAvailable: boolean;
  generate: (options: GenerateOptions) => Promise<string>;
};

const LocalLLMContext = createContext<LocalLLMContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export const LocalLLMProvider: React.FC<Props> = ({ children }) => {
  const [status, setStatus] = useState<LocalLLMStatus>({ state: "idle" });

  useEffect(() => {
    const unsub = localLLM.subscribe(setStatus);
    localLLM.init().catch((err) => {
      console.error("[LocalLLM] init failed:", err);
      setStatus((s) => ({ ...s, state: "error", error: String(err) }));
    });
    return () => {
      unsub();
      localLLM.dispose().catch(() => {});
    };
  }, []);

  const value = useMemo<LocalLLMContextValue>(() => ({
    status,
    isAvailable: status.state === "ready",
    generate: (opts) => localLLM.generate(opts),
  }), [status]);

  return <LocalLLMContext.Provider value={value}>{children}</LocalLLMContext.Provider>;
};

export const useLocalLLM = (): LocalLLMContextValue => {
  const ctx = useContext(LocalLLMContext);
  if (ctx) return ctx;
  // Safe fallback to avoid crashing if provider is missing in a sub-root
  return {
    status: { state: "webgpu_not_supported" },
    isAvailable: false,
    generate: async () => { throw new Error("Local LLM not initialized"); },
  };
};